import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, formatApiError } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Loader2, Search, Download, RotateCcw, RefreshCw, AlertTriangle, ExternalLink } from "lucide-react";

export const PAY_STATUS = {
  initiated: { color: "#8a8a8a", ar: "بدأ الدفع (لم يُحصَّل)", en: "Initiated (not collected)" },
  pending: { color: "#c9a55c", ar: "قيد المعالجة", en: "Processing" },
  held: { color: "#0ea5e9", ar: "محجوز (ضمان)", en: "Held (escrow)" },
  paid: { color: "#16a34a", ar: "مدفوع بنجاح", en: "Paid" },
  failed: { color: "#ef4444", ar: "فشل", en: "Failed" },
  canceled: { color: "#6b6b6b", ar: "ملغي / منتهي", en: "Canceled / expired" },
  refunded: { color: "#8b5cf6", ar: "مسترد", en: "Refunded" },
};
const SERVICES = {
  boost: { ar: "تعزيز إعلان", en: "Listing boost" },
  subscription: { ar: "اشتراك", en: "Subscription" },
  escrow: { ar: "إيداع ضمان", en: "Escrow deposit" },
  ad_campaign: { ar: "حملة إعلانية", en: "Ad campaign" },
  other: { ar: "أخرى", en: "Other" },
};
const SOURCES = { moyasar: "Moyasar", stripe_legacy: "Stripe (قديم)" };

export default function PaymentsAdminPage() {
  const { lang } = useI18n();
  const ar = lang === "ar";
  const [data, setData] = useState({ items: [], total: 0, summary: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [f, setF] = useState({ q: "", status: "", service: "", source: "", date_from: "", date_to: "", unlinked: 0 });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = { limit: 300 };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data: d } = await apiClient.get("/admin/payments", { params });
      setData(d);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); await load(); } catch (e) { alert(formatApiError(e)); } finally { setBusy(null); }
  };
  const reconcileOne = (row) => run(row.id, async () => {
    const { data: r } = await apiClient.post(`/admin/payments/${row.id}/reconcile`);
    if (r.reason === "no_payment_attempt") alert(ar ? "لا توجد محاولة دفع لدى ميسر لهذه العملية — بقيت الحالة كما هي." : "No Moyasar payment attempt — status unchanged.");
  });
  const reconcileAll = () => run("all", async () => {
    const { data: r } = await apiClient.post("/admin/payments/reconcile-stuck");
    alert(ar ? `تم فحص ${r.checked} عملية لدى ميسر.` : `Checked ${r.checked} payments with Moyasar.`);
  });
  const refund = (row) => {
    const amt = prompt(ar ? `مبلغ الاسترداد بالريال (فارغ = كامل ${row.amount_sar}):` : `Refund SAR (blank = full ${row.amount_sar}):`, "");
    if (amt === null) return;
    run(row.id, () => apiClient.post("/admin/payments/refund", { payment_id: row.id, amount_sar: amt.trim() ? Number(amt) : null }));
  };
  const downloadCsv = () => run("csv", async () => {
    const res = await apiClient.get("/admin/payments/export.csv", { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: "cartal-payments.csv" });
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="admin-payments-page">
      <Header ar={ar} summary={data.summary} total={data.total} busy={busy} onSync={reconcileAll} onCsv={downloadCsv} />
      <Filters ar={ar} f={f} setF={setF} summary={data.summary} />
      {err && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 mb-4" data-testid="admin-payments-error">{err}</div>}
      {f.unlinked ? <ReviewNotice ar={ar} /> : null}
      <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="admin-payments-table">
          <thead className="bg-[#fafaf7] border-b border-[#0a0a0a]/10">
            <tr className="text-[10px] uppercase tracking-[0.15em] text-[#6b6b6b]">
              {(ar ? ["التاريخ", "صاحب العملية", "الخدمة / الطلب", "المبلغ", "الحالة", "رقم العملية", ""] : ["Date", "Payer", "Service / order", "Amount", "Status", "Transaction", ""]).map((h, i) => (
                <th key={i} className="text-start p-3 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="p-10 text-center text-[#6b6b6b]"><Loader2 className="w-5 h-5 animate-spin inline me-2" />{ar ? "تحميل…" : "Loading…"}</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="7" className="p-10 text-center text-[#8a8a8a]" data-testid="admin-payments-empty">{ar ? "لا توجد مدفوعات مطابقة." : "No matching payments."}</td></tr>
            ) : data.items.map((r) => (
              <PaymentRow key={`${r.source}-${r.id}`} r={r} ar={ar} busy={busy === r.id} onReconcile={reconcileOne} onRefund={refund} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Header({ ar, summary, total, busy, onSync, onCsv }) {
  const btn = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c] hover:bg-[#1a1a1a] disabled:opacity-50";
  return (
    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
      <div>
        <div className="overline overline-gold">{ar ? "لوحة الإدارة" : "ADMIN"}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-[#0a0a0a]">{ar ? "إدارة المدفوعات" : "Payment management"}</h1>
        <p className="text-sm text-[#6b6b6b] mt-1" data-testid="admin-payments-total">
          {ar ? `النتائج: ${total} من أصل ${summary?.all ?? 0}` : `Results: ${total} of ${summary?.all ?? 0}`}
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button type="button" onClick={onSync} disabled={!!busy} className={btn} data-testid="admin-payments-reconcile-all">
          {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {ar ? "مزامنة العمليات العالقة مع ميسر" : "Sync stuck with Moyasar"}
        </button>
        <button type="button" onClick={onCsv} disabled={!!busy} className={btn} data-testid="admin-payments-csv">
          <Download className="w-4 h-4" />{ar ? "تصدير CSV" : "Export CSV"}
        </button>
      </div>
    </div>
  );
}

function Filters({ ar, f, setF, summary }) {
  const [q, setQ] = useState(f.q);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const chip = (active) => `rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-[#c9a55c] text-[#0a0a0a]" : "border border-[#0a0a0a]/15 text-[#3a3a3a] hover:border-[#c9a55c]"}`;
  const bs = summary?.by_status || {};
  return (
    <div className="rounded-2xl border border-[#0a0a0a]/10 bg-white p-4 mb-4 space-y-3">
      <form onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }} className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a8a8a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="input-dark ps-10 text-sm" data-testid="admin-payments-search"
            placeholder={ar ? "ابحث بالاسم، البريد، الجوال، رقم العملية أو الطلب…" : "Search name, email, phone, transaction or order id…"} />
        </div>
        <select value={f.service} onChange={(e) => set("service", e.target.value)} className="input-dark text-sm max-w-[170px]" data-testid="admin-payments-service">
          <option value="">{ar ? "كل الخدمات" : "All services"}</option>
          {Object.entries(SERVICES).map(([k, v]) => <option key={k} value={k}>{ar ? v.ar : v.en}</option>)}
        </select>
        <select value={f.source} onChange={(e) => set("source", e.target.value)} className="input-dark text-sm max-w-[160px]" data-testid="admin-payments-source">
          <option value="">{ar ? "كل المصادر" : "All sources"}</option>
          {Object.entries(SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} className="input-dark text-sm max-w-[160px]" data-testid="admin-payments-date-from" aria-label={ar ? "من تاريخ" : "From"} />
        <input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} className="input-dark text-sm max-w-[160px]" data-testid="admin-payments-date-to" aria-label={ar ? "إلى تاريخ" : "To"} />
        <button type="submit" className="rounded-full px-4 py-2 text-sm font-bold bg-[#0a0a0a] text-[#c9a55c]" data-testid="admin-payments-search-btn">{ar ? "بحث" : "Search"}</button>
      </form>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => set("status", "")} className={chip(!f.status)} data-testid="admin-payments-tab-all">{ar ? "الكل" : "All"}</button>
        {Object.entries(PAY_STATUS).map(([k, v]) => (
          <button key={k} type="button" onClick={() => set("status", k)} className={chip(f.status === k)} data-testid={`admin-payments-tab-${k}`}>
            {ar ? v.ar : v.en} <span className="opacity-60">({bs[k] || 0})</span>
          </button>
        ))}
        <button type="button" onClick={() => set("unlinked", f.unlinked ? 0 : 1)} className={`${chip(!!f.unlinked)} inline-flex items-center gap-1`} data-testid="admin-payments-unlinked-toggle">
          <AlertTriangle className="w-3 h-3" />{ar ? "غير مرتبطة بمستخدم" : "Unlinked"} ({summary?.unlinked || 0})
        </button>
      </div>
    </div>
  );
}

function ReviewNotice({ ar }) {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 mb-4" data-testid="admin-payments-review-notice">
      {ar
        ? "قائمة مراجعة: عمليات لم يُحفظ معها معرّف مستخدم موثوق. لا يتم تخمين أصحابها؛ السبب ظاهر في كل صف."
        : "Review list: records without a trusted user id. Owners are never guessed; the reason is shown per row."}
    </div>
  );
}

function PaymentRow({ r, ar, busy, onReconcile, onRefund }) {
  const svc = SERVICES[r.service_type] || SERVICES.other;
  const canSync = r.source === "moyasar" && ["initiated", "pending"].includes(r.status) && (r.provider_payment_id || r.moyasar_invoice_id);
  return (
    <tr className="border-b border-[#0a0a0a]/5 hover:bg-[#fafaf7] align-top" data-testid={`admin-payment-row-${r.id}`}>
      <td className="p-3 text-xs text-[#525252] whitespace-nowrap" dir="ltr">{r.created_at ? new Date(r.created_at).toLocaleString(ar ? "ar-SA" : "en-GB") : "—"}</td>
      <td className="p-3 min-w-[200px]"><Payer r={r} ar={ar} /></td>
      <td className="p-3 min-w-[170px]">
        <div className="text-xs font-semibold text-[#5a4615]">{ar ? svc.ar : svc.en}</div>
        {r.sku && <div className="text-[10px] text-[#8a8a8a]">{r.sku}</div>}
        {r.reference_type === "vehicle" && r.reference_id ? (
          <Link to={`/vehicle/${r.reference_id}`} target="_blank" className="text-[11px] text-[#8a6f2e] underline inline-flex items-center gap-1" data-testid={`admin-payment-ref-${r.id}`}>
            {r.reference_label || (ar ? "الإعلان" : "Listing")} <ExternalLink className="w-3 h-3" />
          </Link>
        ) : r.reference_id ? <div className="text-[10px] font-mono text-[#8a8a8a]" dir="ltr">{r.reference_type}: {r.reference_id}</div> : null}
      </td>
      <td className="p-3 font-mono text-sm text-[#0a0a0a] whitespace-nowrap" dir="ltr" data-testid={`admin-payment-amount-${r.id}`}>
        {Number(r.amount_sar || 0).toLocaleString()} {r.currency || "SAR"}
        {r.refunded_halalas > 0 && <div className="text-[10px] text-red-600">-{(r.refunded_halalas / 100).toFixed(2)} {ar ? "مسترد" : "refunded"}</div>}
      </td>
      <td className="p-3"><StatusPill status={r.status} ar={ar} /><div className="text-[10px] text-[#8a8a8a] mt-1" dir="ltr">{r.provider_status || ""}</div></td>
      <td className="p-3 font-mono text-[10px] text-[#525252] max-w-[220px] break-all" dir="ltr">
        <div><span className="text-[#8a8a8a]">{SOURCES[r.source]}:</span> {r.provider_payment_id || "—"}</div>
        {r.moyasar_invoice_id && <div><span className="text-[#8a8a8a]">invoice:</span> {r.moyasar_invoice_id}</div>}
        <div><span className="text-[#8a8a8a]">CARTAL:</span> {r.id}</div>
      </td>
      <td className="p-3 whitespace-nowrap">
        {canSync && (
          <button type="button" onClick={() => onReconcile(r)} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-[#0a0a0a] hover:text-[#c9a55c]" data-testid={`admin-reconcile-${r.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}{ar ? "تحقق من ميسر" : "Verify"}
          </button>
        )}
        {r.source === "moyasar" && r.status === "paid" && (
          <button type="button" onClick={() => onRefund(r)} disabled={busy} className="inline-flex items-center gap-1 text-xs font-bold text-[#8a6f2e] hover:text-[#c9a55c]" data-testid={`admin-refund-${r.id}`}>
            <RotateCcw className="w-3 h-3" />{ar ? "استرداد" : "Refund"}
          </button>
        )}
      </td>
    </tr>
  );
}

function Payer({ r, ar }) {
  if (r.linked) {
    return (
      <>
        <Link to={`/admin?tab=users&q=${encodeURIComponent(r.user_email || r.user_id)}`} className="font-semibold text-[#0a0a0a] hover:text-[#8a6f2e] underline-offset-2 hover:underline" data-testid={`admin-payment-user-${r.id}`}>
          {r.user_name || r.user_email || r.user_id}
        </Link>
        <div className="text-[10px] text-[#8a8a8a]" dir="ltr">{r.user_email}{r.user_phone ? ` · ${r.user_phone}` : ""}</div>
        {r.link_source === "metadata" && <div className="text-[10px] text-[#8a8a8a]">{ar ? "رُبط من بيانات العملية المحفوظة" : "Linked from stored metadata"}</div>}
      </>
    );
  }
  return (
    <div data-testid={`admin-payment-unlinked-${r.id}`}>
      <div className="inline-flex items-center gap-1 text-xs font-bold text-amber-700"><AlertTriangle className="w-3 h-3" />{ar ? "غير محدد" : "Unidentified"}</div>
      {(r.user_name || r.user_email) && <div className="text-[10px] text-[#525252]">{ar ? "آخر بيانات محفوظة:" : "Last stored:"} {r.user_name || ""} {r.user_email || ""}</div>}
      <div className="text-[10px] text-[#8a8a8a] mt-0.5">{r.unlinked_reason}</div>
    </div>
  );
}

function StatusPill({ status, ar }) {
  const s = PAY_STATUS[status] || { color: "#8a8a8a", ar: status, en: status };
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold whitespace-nowrap"
      style={{ backgroundColor: s.color + "22", color: s.color, border: `1px solid ${s.color}55` }} data-testid={`payment-status-${status}`}>
      {ar ? s.ar : s.en}
    </span>
  );
}
